import { useQuery } from '@tanstack/react-query';
import { fetchDisruptionRosterPage } from '@/lib/disruptions/api';
import type { DisruptionPnrGroupRow } from '@/lib/disruptions/types';

// PER-FLIGHT facts for the river's flight bands + the strips' mini bars.
//
// The BFF does not expose `perFlight[]` yet (phase 2 — the facts are already
// folded server-side in DisruptionSpendService.perCase, only the cost reaches the
// wire). Until then we fold them CLIENT-side by walking the disruption roster —
// the same pattern the hotel board uses (limit 200, bounded pages). The query key
// sits under ['disruptions', urn, 'roster'] on purpose: applyEvent invalidates
// that prefix when a run goes terminal / a hotel settles, so the bands follow the
// runs without any poll. Counts only — never a passenger record leaves this fold.

export interface FlightFacts {
  flightUrn: string;
  caseUrn: string;
  code: string;
  groups: number;
  pax: number;
  assigned: number;
  optionsReady: number;
  noOptions: number;
  notApplicable: number;
  pending: number;
  ticketed: number;
  hotelNeeded: number;
  hotelHeld: number;
  hotelConfirmed: number;
  voucherEligible: number;
  voucherIssued: number;
  /* Acceptance bundle — offer as ONE thing (flight + hotel when overnight). */
  /** Every reversible part exists (seat on HOLD; hotel HOLD when needed). */
  offerReady: number;
  /** The offer went out to the passenger (or a supervisor forced it). */
  offered: number;
  accepted: number;
  awaiting: number;
  declined: number;
  /** Ticket issued AND hotel confirmed (automatic after acceptance). */
  issued: number;
  /* Passengers behind the group counts (a group is 1..n people). */
  paxAssigned: number;
  paxOfferReady: number;
  paxAccepted: number;
  paxIssued: number;
  paxNoOptions: number;
  /* MASS ground transport (additive; 0 on a roster without `transfer`). Groups
   *  issued with a hotel that a shuttle must move / already at the hotel / on the
   *  way / left at the pickup — from the roster row's `transfer` slice. */
  transferNeeded?: number;
  transferDone?: number;
  transferEnRoute?: number;
  transferNoShow?: number;
  paxTransferred?: number;
}

export interface PerFlightFacts {
  byFlight: Map<string, FlightFacts>;
  totalGroups: number;
  truncated: boolean;
  computedAt: number;
}

const PAGE_LIMIT = 200;
const MAX_PAGES = 30;

function fresh(row: DisruptionPnrGroupRow): FlightFacts {
  return {
    flightUrn: row.flightUrn,
    caseUrn: row.caseUrn,
    code: row.flightNumber || row.flightLabel,
    groups: 0,
    pax: 0,
    assigned: 0,
    optionsReady: 0,
    noOptions: 0,
    notApplicable: 0,
    pending: 0,
    ticketed: 0,
    hotelNeeded: 0,
    hotelHeld: 0,
    hotelConfirmed: 0,
    voucherEligible: 0,
    voucherIssued: 0,
    offerReady: 0,
    offered: 0,
    accepted: 0,
    awaiting: 0,
    declined: 0,
    issued: 0,
    paxAssigned: 0,
    paxOfferReady: 0,
    paxAccepted: 0,
    paxIssued: 0,
    paxNoOptions: 0,
    transferNeeded: 0,
    transferDone: 0,
    transferEnRoute: 0,
    transferNoShow: 0,
    paxTransferred: 0,
  };
}

const TRANSFER_DONE = new Set(['COMPLETED', 'ARRIVED']);
const TRANSFER_MOVING = new Set([
  'REQUESTED',
  'DISPATCHED',
  'IN_TRANSIT',
  'VEHICLE_ASSIGNED',
  'BOARDING',
  'DEPARTED',
]);

const ACCEPTED_STATUSES = new Set(['OFFER_READY', 'RESOLVED']);
const OFFERED_STATUSES = new Set(['AWAITING_ACCEPTANCE', 'OFFER_READY', 'RESOLVED', 'DECLINED']);

/** Bundle facts of ONE row — the cases-owned `offer`/`commit` when present, else
 *  derived from the legacy denorm (ticket number, hotel reservation status). */
export function rowBundle(row: DisruptionPnrGroupRow): {
  assigned: boolean;
  needsHotel: boolean;
  hotelCovered: boolean;
  offerReady: boolean;
  offered: boolean;
  accepted: boolean;
  awaiting: boolean;
  declined: boolean;
  issued: boolean;
  /** Issued + hotel: a shuttle must move the group (its airport→hotel leg). */
  transferNeeded: boolean;
  transferDone: boolean;
  transferEnRoute: boolean;
  transferNoShow: boolean;
} {
  const assigned = row.rebookingStatus === 'SELECTED' || row.rebookingStatus === 'OVERRIDDEN';
  const needsHotel = !!row.requiresHotel || row.accommodationKind === 'HOTEL';
  const hotelCovered =
    row.hotelReservationStatus === 'HELD' || row.hotelReservationStatus === 'CONFIRMED';
  const offerReady = row.offer ? row.offer.ready : assigned && (!needsHotel || hotelCovered);
  const st = row.subCaseStatus;
  const offered = st ? OFFERED_STATUSES.has(st) : false;
  const accepted = st ? ACCEPTED_STATUSES.has(st) : false;
  const awaiting = st === 'AWAITING_ACCEPTANCE';
  const declined = st === 'DECLINED';
  const issued = row.commit
    ? row.commit.complete
    : !!row.ticketNumber && (!needsHotel || row.hotelReservationStatus === 'CONFIRMED');
  const tr = row.transfer && row.transfer.leg !== 'HOTEL_TO_AIRPORT' ? row.transfer : undefined;
  const transferNeeded = issued && (needsHotel || !!tr);
  const transferNoShow = !!tr && (tr.boardingStatus === 'NO_SHOW' || tr.status === 'NO_SHOW');
  const transferDone = !!tr && TRANSFER_DONE.has(tr.status) && !transferNoShow;
  const transferEnRoute = !!tr && TRANSFER_MOVING.has(tr.status) && !transferNoShow;
  return {
    assigned,
    needsHotel,
    hotelCovered,
    offerReady,
    offered,
    accepted,
    awaiting,
    declined,
    issued,
    transferNeeded,
    transferDone,
    transferEnRoute,
    transferNoShow,
  };
}

/** Pure fold — exported for tests. */
export function foldPerFlight(rows: DisruptionPnrGroupRow[]): Map<string, FlightFacts> {
  const by = new Map<string, FlightFacts>();
  for (const r of rows) {
    const key = r.flightUrn || r.caseUrn;
    const f = by.get(key) ?? fresh(r);
    f.groups += 1;
    f.pax += r.paxCount ?? 0;
    switch (r.rebookingStatus) {
      case 'SELECTED':
      case 'OVERRIDDEN':
        f.assigned += 1;
        break;
      case 'OPTIONS_READY':
        f.optionsReady += 1;
        break;
      case 'NO_OPTIONS':
        f.noOptions += 1;
        break;
      case 'NOT_APPLICABLE':
        f.notApplicable += 1;
        break;
      default:
        f.pending += 1;
    }
    if (r.ticketNumber) f.ticketed += 1;
    if (r.requiresHotel || r.accommodationKind === 'HOTEL') f.hotelNeeded += 1;
    if (r.hotelReservationStatus === 'HELD') f.hotelHeld += 1;
    if (r.hotelReservationStatus === 'CONFIRMED') f.hotelConfirmed += 1;
    if (r.accommodationKind === 'MEAL_VOUCHER' || r.mealVoucher) f.voucherEligible += 1;
    if (r.mealVoucher?.issued) f.voucherIssued += 1;
    const b = rowBundle(r);
    const pax = r.paxCount ?? 0;
    if (b.offerReady) f.offerReady += 1;
    if (b.offered) f.offered += 1;
    if (b.accepted) f.accepted += 1;
    if (b.awaiting) f.awaiting += 1;
    if (b.declined) f.declined += 1;
    if (b.issued) f.issued += 1;
    if (b.assigned) f.paxAssigned += pax;
    if (b.offerReady) f.paxOfferReady += pax;
    if (b.accepted) f.paxAccepted += pax;
    if (b.issued) f.paxIssued += pax;
    if (r.rebookingStatus === 'NO_OPTIONS') f.paxNoOptions += pax;
    if (b.transferNeeded) f.transferNeeded = (f.transferNeeded ?? 0) + 1;
    if (b.transferDone) {
      f.transferDone = (f.transferDone ?? 0) + 1;
      f.paxTransferred = (f.paxTransferred ?? 0) + pax;
    }
    if (b.transferEnRoute) f.transferEnRoute = (f.transferEnRoute ?? 0) + 1;
    if (b.transferNoShow) f.transferNoShow = (f.transferNoShow ?? 0) + 1;
    by.set(key, f);
  }
  return by;
}

export async function buildPerFlight(urn: string): Promise<PerFlightFacts> {
  const rows: DisruptionPnrGroupRow[] = [];
  let cursor: string | undefined;
  let pages = 0;
  let truncated = false;
  for (;;) {
    const page = await fetchDisruptionRosterPage(urn, {
      ...(cursor ? { cursor } : {}),
      limit: PAGE_LIMIT,
    } as Parameters<typeof fetchDisruptionRosterPage>[1]);
    rows.push(...page.items);
    pages += 1;
    if (!page.nextCursor) break;
    if (pages >= MAX_PAGES) {
      truncated = true;
      break;
    }
    cursor = page.nextCursor;
  }
  return {
    byFlight: foldPerFlight(rows),
    totalGroups: rows.length,
    truncated,
    computedAt: Date.now(),
  };
}

export function usePerFlightFacts(urn: string | undefined, enabled = true) {
  return useQuery<PerFlightFacts>({
    queryKey: ['disruptions', urn, 'roster', 'per-flight'],
    queryFn: () => buildPerFlight(urn!),
    enabled: !!urn && enabled,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
}
